<script setup lang="ts">
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import { useBaseStore } from "@/stores/base";
import { computed, markRaw } from "vue";

import RecentExplorerConfigurations from "@/modules/home/components/RecentExplorerConfigurations.vue";
import RecentDashboards from "@/modules/home/components/RecentDashboards.vue";
import Notifications from "@/modules/home/components/Notifications.vue";
import Alerts from "@/modules/home/components/Alerts.vue";
import RecentBookings from "@/modules/home/components/RecentBookings.vue";
import PriceUpdates from "@/modules/home/components/PriceUpdates.vue";
import Tasks from "@/modules/home/components/Tasks.vue";
import Labels from "@/modules/home/components/Labels.vue";
import RecentPriceUpdateErrors from "@/modules/home/components/RecentPriceUpdateErrors.vue";
import MinMaxOffsetReached from "@/modules/home/components/MinMaxOffsetReached.vue";
import Exports from "@/modules/home/components/Exports.vue";

type Widget = {
	key: string;
	component: unknown;
	cols: number;
	rows: number;
	desiredPosition: number;
	enabled: boolean;
};

const baseStore = useBaseStore();

const breadcrumbs = [
	{
		title: "Home",
		disabled: true,
		to: "/home",
	},
];

const userCanAccessExplorer = computed(() =>
	baseStore.currentUser?.derivedPermissions.includes("can_manage_explorer")
);

const userCanAccessDashboards = computed(() =>
	baseStore.currentUser?.derivedPermissions.includes("can_manage_dashboards")
);

const userCanAccessAlerts = computed(() =>
	baseStore.currentUser?.derivedPermissions.includes("can_manage_alerts")
);

const userCanAccessBexModule = computed(() =>
	baseStore.currentUser?.derivedPermissions.includes(
		"can_manage_booking_experts"
	)
);

const userCanAccessLoyaltyModule = computed(() =>
	baseStore.currentUser?.derivedPermissions.includes("can_manage_pro_loyalty")
);

const userCanAccessScheduler = computed(() =>
	baseStore.currentUser?.derivedPermissions.includes("can_manage_scheduler")
);

const widgets = computed(() => {
	const items: Widget[] = [
		{
			key: "price-updates",
			component: markRaw(PriceUpdates),
			cols: 1,
			rows: 3,
			desiredPosition: 1,
			enabled: !!userCanAccessBexModule.value,
		},
		{
			key: "tasks",
			component: markRaw(Tasks),
			cols: 1,
			rows: 3,
			desiredPosition: 2,
			enabled: !!userCanAccessLoyaltyModule.value,
		},
		{
			key: "labels",
			component: markRaw(Labels),
			cols: 1,
			rows: 3,
			desiredPosition: 3,
			enabled: !!userCanAccessLoyaltyModule.value,
		},
		{
			key: "recent-bookings",
			component: markRaw(RecentBookings),
			cols: 2,
			rows: 3,
			desiredPosition: 4,
			enabled: !!userCanAccessBexModule.value,
		},
		{
			key: "min-max-offset-reached",
			component: markRaw(MinMaxOffsetReached),
			cols: 2,
			rows: 3,
			desiredPosition: 5,
			enabled: !!userCanAccessBexModule.value,
		},
		{
			key: "recent-dashboards",
			component: markRaw(RecentDashboards),
			cols: 1,
			rows: 3,
			desiredPosition: 6,
			enabled: !!userCanAccessDashboards.value,
		},
		{
			key: "recent-price-update-errors",
			component: markRaw(RecentPriceUpdateErrors),
			cols: 2,
			rows: 3,
			desiredPosition: 7,
			enabled: !!userCanAccessBexModule.value,
		},
		{
			key: "recent-explorer-configurations",
			component: markRaw(RecentExplorerConfigurations),
			cols: 1,
			rows: 3,
			desiredPosition: 8,
			enabled: !!userCanAccessExplorer.value,
		},
		{
			key: "notifications",
			component: markRaw(Notifications),
			cols: 1,
			rows: 1,
			desiredPosition: 9,
			enabled: true,
		},
		{
			key: "alerts",
			component: markRaw(Alerts),
			cols: 1,
			rows: 1,
			desiredPosition: 10,
			enabled: !!userCanAccessAlerts.value,
		},
		{
			key: "upcoming-exports",
			component: markRaw(Exports),
			cols: 2,
			rows: 4,
			desiredPosition: 11,
			enabled: !!userCanAccessScheduler.value,
		},
	];

	return optimizeGrid(items.filter((item) => item.enabled));
});

function optimizeGrid(items: Widget[], totalCols = 3): Widget[] {
	const remaining = [...items].sort(
		(a, b) => a.desiredPosition - b.desiredPosition
	);
	const result: Widget[] = [];

	while (remaining.length > 0) {
		let colsUsed = 0;
		const placed: Widget[] = [];

		for (const item of remaining) {
			if (colsUsed + item.cols <= totalCols) {
				placed.push(item);
				colsUsed += item.cols;
				if (colsUsed >= totalCols) break;
			}
		}

		if (placed.length === 0) {
			placed.push(remaining[0]);
		}

		result.push(...placed);
		const placedSet = new Set(placed);
		remaining.splice(
			0,
			remaining.length,
			...remaining.filter((i) => !placedSet.has(i))
		);
	}

	return result;
}
</script>

<template>
	<main-breadcrumbs :breadcrumbs="breadcrumbs" is-user />

	<div class="grid-container">
		<component
			:is="widget.component"
			v-for="widget in widgets"
			:key="widget.key"
			class="grid-item"
			:style="{
				gridColumn: `span ${widget.cols}`,
				gridRow: `span ${widget.rows}`,
			}"
		/>
	</div>
</template>

<style scoped>
.grid-container {
	display: grid;
	grid-template-columns: repeat(3, minmax(300px, 1fr));
	grid-auto-rows: 70px;
	gap: 1rem;
	grid-auto-flow: dense;
	overflow-y: visible;
}

.grid-item {
	min-width: 0;
}

@media (max-width: 1100px) {
	.grid-container {
		grid-template-columns: repeat(2, minmax(300px, 1fr));
	}
}

@media (max-width: 700px) {
	.grid-container {
		grid-template-columns: 1fr;
	}

	.grid-item {
		grid-column: span 1 !important;
	}
}
</style>
